import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Activity, AlertCircle, ShieldAlert, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  normal: "bg-emerald-50 text-emerald-800 border-emerald-200",
  elevated: "bg-amber-50 text-amber-800 border-amber-200",
  near_limit: "bg-orange-50 text-orange-800 border-orange-200",
  limited: "bg-red-50 text-red-800 border-red-200",
};
const STATUS_LABEL = { normal: "Normal", elevated: "Elevated", near_limit: "Near limit", limited: "Limited" };

function Stat({ label, value, icon: Icon, tone = "default", testid }) {
  const t = { default: "border-border", warn: "border-amber-200 bg-amber-50/40",
              danger: "border-red-200 bg-red-50/40", ok: "border-emerald-200 bg-emerald-50/40" }[tone];
  return (
    <Card data-testid={testid} className={`p-4 ${t}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="mt-1 text-2xl font-serif font-black">{value ?? 0}</p>
        </div>
        {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
      </div>
    </Card>
  );
}

export default function AiUsage() {
  const { user } = useAuth();
  const canAccess = user && (user.role === "admin" || user.role === "faculty");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    try {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (status) p.set("status", status);
      p.set("limit", String(limit));
      p.set("offset", String(offset));
      const { data } = await api.get(`/admin/ai-usage?${p.toString()}`);
      setData(data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail));
      setData(null);
    }
  }, [q, status, offset]);

  useEffect(() => { if (canAccess) load(); }, [canAccess, load]);

  if (!user) return null;
  if (!canAccess) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="ai-usage-forbidden">
        <Card className="p-8 text-center">
          <ShieldAlert className="h-8 w-8 mx-auto text-muted-foreground" />
          <h1 className="mt-3 font-serif text-2xl font-black">AI Trust & Safety</h1>
          <p className="mt-2 text-sm text-muted-foreground">Available to platform administrators only.</p>
        </Card>
      </div>
    );
  }

  const s = data?.summary || {};
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="ai-usage-page">
      <header className="flex items-center gap-3">
        <Activity className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-serif text-3xl font-black tracking-tight">AI Trust &amp; Safety</h1>
          <p className="text-sm text-muted-foreground">Operational visibility. Observation only — no quota bypass.</p>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-total" label="Requests today" value={s.total_ai_requests_today} icon={TrendingUp} />
        <Stat testid="stat-active" label="Users active today" value={s.users_active_today} icon={Activity} tone="ok" />
        <Stat testid="stat-near" label="Users near daily limit" value={s.users_near_daily_limit} icon={AlertCircle} tone="warn" />
        <Stat testid="stat-limited" label="Users rate-limited today" value={s.users_rate_limited_today} icon={ShieldAlert} tone="danger" />
      </section>

      <Card className="p-4" data-testid="filters">
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-1">
            {["", "normal", "elevated", "near_limit", "limited"].map(v => (
              <Button key={v || "all"} size="sm" variant={status === v ? "default" : "outline"}
                data-testid={`filter-${v || "all"}`} onClick={() => { setStatus(v); setOffset(0); }}>
                {v ? STATUS_LABEL[v] : "All"}
              </Button>
            ))}
          </div>
          <Input data-testid="search-input" className="h-9 max-w-xs" placeholder="Search user / email / id"
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { setOffset(0); load(); } }} />
          <Button size="sm" variant="outline" onClick={() => { setOffset(0); load(); }}>Apply</Button>
          <span className="ml-auto text-[11px] text-muted-foreground">
            Daily quota: {data?.config?.daily_quota} · Per-min: {data?.config?.per_minute_quota}
          </span>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden" data-testid="usage-table">
        <div className="border-b p-3 text-xs uppercase tracking-widest text-muted-foreground">
          {data ? `${data.total} user${data.total === 1 ? "" : "s"}` : "Loading…"}
        </div>
        {data?.results?.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="usage-empty">
            No AI usage matches your filters today.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                  <th className="p-3">User</th>
                  <th className="p-3">Daily</th>
                  <th className="p-3">%</th>
                  <th className="p-3">This minute</th>
                  <th className="p-3">Recent 429s</th>
                  <th className="p-3">Last request</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data?.results || []).map(r => (
                  <tr key={r.user_id} className="border-t" data-testid={`row-${r.user_id}`}>
                    <td className="p-3 min-w-[220px]">
                      <p className="font-medium truncate">{r.display_name || "—"}</p>
                      <p className="text-xs text-muted-foreground truncate">{r.email || r.user_id}</p>
                    </td>
                    <td className="p-3">{r.daily_count} / {r.daily_quota}</td>
                    <td className="p-3">{r.daily_percent}%</td>
                    <td className="p-3">{r.current_minute_count} / {r.per_minute_quota}</td>
                    <td className="p-3">{r.recent_rate_limit_events}</td>
                    <td className="p-3 text-xs">{r.last_ai_request_at ? new Date(r.last_ai_request_at).toLocaleString() : "—"}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${STATUS_STYLE[r.usage_status] || ""}`}>
                        {STATUS_LABEL[r.usage_status] || r.usage_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="border-t p-3 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">
            Showing {data?.results?.length || 0} of {data?.total || 0}
          </span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</Button>
            <Button size="sm" variant="outline" disabled={!data || offset + limit >= (data?.total || 0)}
              onClick={() => setOffset(offset + limit)}>Next</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
